import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    EventEmitter,
    Input,
    Output,
    TemplateRef
} from '@angular/core';
import { Logger } from '@df/core/service/logger';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
/**
 * Sample usage:
 *
 *     <dropdown [value]="sort.option"
 *               (valueChange)="sort.by($event)"
 *               [options]="sort.getOptions()">
 *         <ng-template #titleTpl>{{sort.selectionLabel}}</ng-template>
 *         <ng-template #optionTpl let-option>{{option.label}}</ng-template>
 *     </dropdown>
 */
@Component({
    selector: 'dropdown',
    templateUrl: './dropdown.html',
    styleUrls: ['./dropdown.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DropdownComponent<T> {
    /**
     * No selection placeholder
     */
    @Input()
    placeholder?: string;

    /**
     * Selected value
     */
    @Input()
    value?: T;

    /**
     * Value output
     */
    @Output()
    valueChange = new EventEmitter<T>();

    /**
     * Open state output
     */
    @Output() openStateChange = new EventEmitter<boolean>();

    /**
     * Options to print
     */
    @Input()
    options?: T[];

    @Input()
    bodyZIndex?: number = 1;

    /**
     * If is open
     */
    readonly openState = new BehaviorSubject(false);

    /**
     * Title presentation teplate
     */
    @ContentChild('titleTpl', { static: false })
    titleTpl?: TemplateRef<any>;

    /**
     * Option presentation template
     */
    @ContentChild('optionTpl', { static: false })
    optionTpl?: TemplateRef<any>;

    /**
     * Dependencies
     */
    constructor(
        private cd: ChangeDetectorRef,
        private logger: Logger
    ) {
        this.openState.pipe(distinctUntilChanged()).subscribe(value => {
            this.openStateChange.emit(value);
        });
    }

    /**
     * Toggle state
     */
    toggle(to = !this.openState.value) {
        this.openState.next(to);
        this.detectChanges();
    }

    pick(option: T) {
        this.toggle(false);
        this.valueChange.emit((this.value = option));
        this.detectChanges();
    }

    private detectChanges() {
        this.cd.detectChanges();
    }
}
